import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Users, 
  FileText, 
  ShoppingCart, 
  DollarSign,
  Search,
  Edit,
  Save,
  X,
  Coins,
  RefreshCw,
  Database,
  Eye,
  ChevronDown,
  ChevronUp,
  Plus
} from 'lucide-react';

import { API_URL } from '../config';

export default function AdminPage() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [payslips, setPayslips] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [editingUser, setEditingUser] = useState(null);
  const [editCredits, setEditCredits] = useState('');
  const [saving, setSaving] = useState(false);
  
  // New states for user management modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [createUserForm, setCreateUserForm] = useState({ name: '', email: '', password: '', role: 'user', credits: 0 });
  const [editUserForm, setEditUserForm] = useState({ id: '', name: '', email: '', password: '', role: 'user', credits: 0, is_active: true });
  const [activeTab, setActiveTab] = useState('users');
  const [expandedUser, setExpandedUser] = useState(null);
  const [userDetails, setUserDetails] = useState({});

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [statsRes, usersRes] = await Promise.all([
        axios.get(`${API_URL}/admin/stats`),
        axios.get(`${API_URL}/admin/users`)
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data.users);
      setError('');
    } catch (err) {
      if (err.response?.status === 403) {
        setError('Accesso non autorizzato. Solo gli amministratori possono accedere a questa pagina.');
      } else {
        setError('Errore nel caricamento dei dati');
      }
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllPayslips = async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/payslips`);
      setPayslips(res.data.payslips);
    } catch (err) {
      console.error('Error fetching payslips:', err);
    }
  };

  const fetchAllOrders = async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/orders`);
      setOrders(res.data.orders);
    } catch (err) {
      console.error('Error fetching orders:', err);
    }
  };

  const fetchUserDetails = async (userId) => {
    try {
      const res = await axios.get(`${API_URL}/admin/users/${userId}`);
      setUserDetails(prev => ({ ...prev, [userId]: res.data }));
    } catch (err) {
      console.error('Error fetching user details:', err);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === 'payslips' && payslips.length === 0) {
      fetchAllPayslips();
    } else if (tab === 'orders' && orders.length === 0) {
      fetchAllOrders();
    }
  };

  const handleEditUser = (user) => {
    setEditUserForm({
      id: user.id,
      name: user.name || '',
      email: user.email || '',
      password: '',
      role: user.role || 'user',
      credits: user.credits || 0,
      is_active: user.is_active !== false
    });
    setShowEditModal(true);
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      await axios.post(`${API_URL}/admin/users`, {
        name: createUserForm.name,
        email: createUserForm.email,
        password: createUserForm.password,
        role: createUserForm.role,
        credits: parseInt(createUserForm.credits) || 0
      });
      setShowCreateModal(false);
      fetchData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Errore nella creazione dell\'utente');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      const payload = {
        name: editUserForm.name,
        email: editUserForm.email,
        role: editUserForm.role,
        credits: parseInt(editUserForm.credits) || 0,
        is_active: editUserForm.is_active
      };
      if (editUserForm.password && editUserForm.password.trim() !== '') {
        payload.password = editUserForm.password;
      }
      await axios.put(`${API_URL}/admin/users/${editUserForm.id}`, payload);
      setShowEditModal(false);
      fetchData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Errore nell\'aggiornamento dell\'utente');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const toggleUserDetails = async (userId) => {
    if (expandedUser === userId) {
      setExpandedUser(null);
    } else {
      setExpandedUser(userId);
      if (!userDetails[userId]) {
        await fetchUserDetails(userId);
      }
    }
  };

  const filteredUsers = users.filter(user => 
    user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('it-IT', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="spinner-lg" />
        </div>
      </DashboardLayout>
    );
  }

  if (error && error.includes('non autorizzato')) {
    return (
      <DashboardLayout>
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <X className="w-8 h-8 text-red-600" />
          </div>
          <h1 className="text-2xl font-bold text-red-600 mb-2">Accesso Negato</h1>
          <p className="text-muted-foreground">{error}</p>
          <Button 
            className="mt-6 swiss-btn-primary"
            onClick={() => navigate('/dashboard')}
          >
            Torna alla Dashboard
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Pannello Admin</h1>
            <p className="text-muted-foreground">Gestisci utenti, buste paga e ordini</p>
          </div>
          <Button 
            variant="outline" 
            onClick={fetchData}
            className="gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Aggiorna
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="swiss-card">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="label-uppercase mb-2">Utenti Totali</p>
                  <p className="stat-highlight">{stats?.total_users || 0}</p>
                </div>
                <div className="w-10 h-10 bg-blue-100 rounded-sm flex items-center justify-center">
                  <Users className="w-5 h-5 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="swiss-card">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="label-uppercase mb-2">Buste Analizzate</p>
                  <p className="stat-highlight">{stats?.total_payslips || 0}</p>
                </div>
                <div className="w-10 h-10 bg-mint rounded-sm flex items-center justify-center">
                  <FileText className="w-5 h-5 text-forest" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="swiss-card">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="label-uppercase mb-2">Ordini Completati</p>
                  <p className="stat-highlight">{stats?.total_orders || 0}</p>
                </div>
                <div className="w-10 h-10 bg-amber-100 rounded-sm flex items-center justify-center">
                  <ShoppingCart className="w-5 h-5 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="swiss-card">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="label-uppercase mb-2">Ricavi Totali</p>
                  <p className="stat-highlight text-lg">{formatCurrency(stats?.total_revenue)}</p>
                </div>
                <div className="w-10 h-10 bg-emerald-100 rounded-sm flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-emerald-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={handleTabChange}>
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="users" className="gap-2">
              <Users className="w-4 h-4" />
              Utenti
            </TabsTrigger>
            <TabsTrigger value="payslips" className="gap-2">
              <FileText className="w-4 h-4" />
              Buste Paga
            </TabsTrigger>
            <TabsTrigger value="orders" className="gap-2">
              <ShoppingCart className="w-4 h-4" />
              Ordini
            </TabsTrigger>
            <TabsTrigger value="database" className="gap-2">
              <Database className="w-4 h-4" />
              Database
            </TabsTrigger>
          </TabsList>

          {/* Users Tab */}
          <TabsContent value="users">
            <Card className="swiss-card">
              <CardHeader className="border-b">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <CardTitle>Gestione Utenti</CardTitle>
                  <div className="flex items-center gap-3">
                    <Button
                      onClick={() => {
                        setCreateUserForm({ name: '', email: '', password: '', role: 'user', credits: 0 });
                        setShowCreateModal(true);
                      }}
                      className="swiss-btn-primary flex items-center gap-2"
                    >
                      <Plus className="w-4 h-4" /> Crea Utente
                    </Button>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        placeholder="Cerca utenti..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-9 w-full sm:w-64"
                      />
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50 border-b">
                      <tr>
                        <th className="text-left p-4 font-medium text-sm">Utente</th>
                        <th className="text-left p-4 font-medium text-sm">Email</th>
                        <th className="text-center p-4 font-medium text-sm">Ruolo</th>
                        <th className="text-center p-4 font-medium text-sm">Crediti</th>
                        <th className="text-left p-4 font-medium text-sm">Registrato</th>
                        <th className="text-center p-4 font-medium text-sm">Azioni</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {filteredUsers.map((user) => (
                        <>
                          <tr key={user.id} className="hover:bg-slate-50">
                            <td className="p-4">
                              <div className="font-medium">{user.name || '-'}</div>
                              <div className="text-xs text-muted-foreground">
                                {user.auth_provider === 'google' ? '🔵 Google' : '📧 Email'}
                                {user.is_active === false && ' ❌ Disattivato'}
                              </div>
                            </td>
                            <td className="p-4 text-sm">{user.email}</td>
                            <td className="p-4 text-center">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                user.role === 'admin' ? 'bg-red-100 text-red-800' :
                                user.role === 'operator' ? 'bg-blue-100 text-blue-800' :
                                'bg-slate-100 text-slate-800'
                              }`}>
                                {user.role === 'admin' ? 'Admin' :
                                 user.role === 'operator' ? 'Operatore' :
                                 'Utente'}
                              </span>
                            </td>
                            <td className="p-4 text-center">
                              <span className="inline-flex items-center gap-1 px-3 py-1 bg-mint rounded-full">
                                <Coins className="w-3.5 h-3.5 text-forest" />
                                <span className="font-mono font-bold text-forest">{user.credits || 0}</span>
                              </span>
                            </td>
                            <td className="p-4 text-sm text-muted-foreground">
                              {formatDate(user.created_at)}
                            </td>
                            <td className="p-4 text-center">
                              <div className="flex items-center justify-center gap-2">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleEditUser(user)}
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => toggleUserDetails(user.id)}
                                >
                                  {expandedUser === user.id ? (
                                    <ChevronUp className="w-4 h-4" />
                                  ) : (
                                    <Eye className="w-4 h-4" />
                                  )}
                                </Button>
                              </div>
                            </td>
                          </tr>
                          {expandedUser === user.id && userDetails[user.id] && (
                            <tr key={`${user.id}-details`}>
                              <td colSpan="5" className="p-4 bg-slate-50">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                  <div>
                                    <h4 className="font-medium mb-2">Dettagli Utente</h4>
                                    <pre className="text-xs bg-white p-3 rounded border overflow-auto max-h-40">
                                      {JSON.stringify(userDetails[user.id].user, null, 2)}
                                    </pre>
                                  </div>
                                  <div>
                                    <h4 className="font-medium mb-2">
                                      Buste Paga: {userDetails[user.id].payslips_count}
                                    </h4>
                                    <h4 className="font-medium mb-2 mt-4">
                                      Ordini Recenti: {userDetails[user.id].orders?.length || 0}
                                    </h4>
                                    {userDetails[user.id].orders?.length > 0 && (
                                      <div className="text-xs space-y-1">
                                        {userDetails[user.id].orders.slice(0, 3).map((order, i) => (
                                          <div key={i} className="flex justify-between bg-white p-2 rounded border">
                                            <span>{formatCurrency(order.amount)}</span>
                                            <span className={order.status === 'completed' ? 'text-green-600' : 'text-amber-600'}>
                                              {order.status}
                                            </span>
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </>
                      ))}
                      {filteredUsers.length === 0 && (
                        <tr>
                          <td colSpan="5" className="p-8 text-center text-muted-foreground">
                            {searchTerm ? 'Nessun utente trovato' : 'Nessun utente registrato'}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Payslips Tab */}
          <TabsContent value="payslips">
            <Card className="swiss-card">
              <CardHeader className="border-b">
                <CardTitle className="flex items-center gap-2">
                  <Database className="w-5 h-5" />
                  Tutte le Buste Paga
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50 border-b">
                      <tr>
                        <th className="text-left p-4 font-medium text-sm">Dipendente</th>
                        <th className="text-left p-4 font-medium text-sm">Azienda</th>
                        <th className="text-left p-4 font-medium text-sm">Periodo</th>
                        <th className="text-right p-4 font-medium text-sm">Netto</th>
                        <th className="text-center p-4 font-medium text-sm">Stato</th>
                        <th className="text-left p-4 font-medium text-sm">User ID</th>
                        <th className="text-left p-4 font-medium text-sm">Data</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {payslips.map((p) => (
                        <tr key={p.id} className="hover:bg-slate-50">
                          <td className="p-4 text-sm">{p.data?.employee_name || '-'}</td>
                          <td className="p-4 text-sm">{p.data?.company_name || '-'}</td>
                          <td className="p-4 text-sm">{p.data?.period_month}/{p.data?.period_year}</td>
                          <td className="p-4 text-sm text-right font-mono">
                            {formatCurrency(p.data?.net_salary)}
                          </td>
                          <td className="p-4 text-center">
                            {p.verification?.is_valid ? (
                              <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs">OK</span>
                            ) : (
                              <span className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs">
                                {p.verification?.total_issues || 0} issue
                              </span>
                            )}
                          </td>
                          <td className="p-4 text-xs font-mono text-muted-foreground">{p.user_id?.slice(0, 8)}...</td>
                          <td className="p-4 text-sm text-muted-foreground">{formatDate(p.created_at)}</td>
                        </tr>
                      ))}
                      {payslips.length === 0 && (
                        <tr>
                          <td colSpan="7" className="p-8 text-center text-muted-foreground">
                            Nessuna busta paga trovata
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Orders Tab */}
          <TabsContent value="orders">
            <Card className="swiss-card">
              <CardHeader className="border-b">
                <CardTitle className="flex items-center gap-2">
                  <ShoppingCart className="w-5 h-5" />
                  Tutti gli Ordini
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50 border-b">
                      <tr>
                        <th className="text-left p-4 font-medium text-sm">Order ID</th>
                        <th className="text-left p-4 font-medium text-sm">User ID</th>
                        <th className="text-right p-4 font-medium text-sm">Importo</th>
                        <th className="text-center p-4 font-medium text-sm">Crediti</th>
                        <th className="text-center p-4 font-medium text-sm">Stato</th>
                        <th className="text-left p-4 font-medium text-sm">Data</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {orders.map((order) => (
                        <tr key={order.id} className="hover:bg-slate-50">
                          <td className="p-4 text-xs font-mono">{order.paypal_order_id?.slice(0, 15)}...</td>
                          <td className="p-4 text-xs font-mono text-muted-foreground">{order.user_id?.slice(0, 8)}...</td>
                          <td className="p-4 text-sm text-right font-mono">{formatCurrency(order.amount)}</td>
                          <td className="p-4 text-center">
                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-mint rounded-full text-xs">
                              <Coins className="w-3 h-3 text-forest" />
                              <span className="font-mono font-bold text-forest">{order.credits}</span>
                            </span>
                          </td>
                          <td className="p-4 text-center">
                            <span className={`px-2 py-1 rounded text-xs ${
                              order.status === 'completed' 
                                ? 'bg-green-100 text-green-700' 
                                : 'bg-amber-100 text-amber-700'
                            }`}>
                              {order.status}
                            </span>
                          </td>
                          <td className="p-4 text-sm text-muted-foreground">{formatDate(order.created_at)}</td>
                        </tr>
                      ))}
                      {orders.length === 0 && (
                        <tr>
                          <td colSpan="6" className="p-8 text-center text-muted-foreground">
                            Nessun ordine trovato
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Database Tab */}
          <TabsContent value="database">
            <Card className="swiss-card">
              <CardHeader className="border-b">
                <CardTitle className="flex items-center gap-2">
                  <Database className="w-5 h-5" />
                  Accesso Database MongoDB
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="space-y-6">
                  {/* Connection Info */}
                  <div className="bg-slate-50 p-4 rounded-lg">
                    <h3 className="font-semibold mb-3">Informazioni di Connessione</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-500">Host:</span>
                        <code className="ml-2 bg-gray-200 px-2 py-1 rounded">178.104.196.116</code>
                      </div>
                      <div>
                        <span className="text-gray-500">Porta:</span>
                        <code className="ml-2 bg-gray-200 px-2 py-1 rounded">27017</code>
                      </div>
                      <div>
                        <span className="text-gray-500">Database:</span>
                        <code className="ml-2 bg-gray-200 px-2 py-1 rounded">bustacontrollo</code>
                      </div>
                      <div>
                        <span className="text-gray-500">Collezioni:</span>
                        <code className="ml-2 bg-gray-200 px-2 py-1 rounded">users, payslips, orders</code>
                      </div>
                    </div>
                  </div>

                  {/* MongoDB Compass Instructions */}
                  <div className="border rounded-lg p-4">
                    <h3 className="font-semibold mb-3 flex items-center gap-2">
                      <img src="https://www.mongodb.com/assets/images/global/favicon.ico" alt="MongoDB" className="w-5 h-5" />
                      Accesso con MongoDB Compass
                    </h3>
                    <ol className="list-decimal list-inside space-y-2 text-sm text-gray-700">
                      <li>Scarica <a href="https://www.mongodb.com/try/download/compass" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">MongoDB Compass</a></li>
                      <li>Apri un tunnel SSH verso il server:
                        <code className="block mt-1 bg-gray-100 p-2 rounded text-xs">
                          ssh -L 27017:localhost:27017 root@178.104.196.116
                        </code>
                      </li>
                      <li>In MongoDB Compass, connettiti a:
                        <code className="block mt-1 bg-gray-100 p-2 rounded text-xs">
                          mongodb://localhost:27017/bustacontrollo
                        </code>
                      </li>
                    </ol>
                  </div>

                  {/* Docker Access */}
                  <div className="border rounded-lg p-4">
                    <h3 className="font-semibold mb-3">Accesso via Docker (SSH)</h3>
                    <div className="space-y-3 text-sm">
                      <div>
                        <p className="text-gray-600 mb-1">Connetti al container MongoDB:</p>
                        <code className="block bg-gray-100 p-2 rounded text-xs">
                          ssh root@178.104.196.116<br/>
                          docker exec -it busta_mongo_1 mongosh
                        </code>
                      </div>
                      <div>
                        <p className="text-gray-600 mb-1">Comandi utili:</p>
                        <code className="block bg-gray-100 p-2 rounded text-xs whitespace-pre-wrap">
{`use bustacontrollo
db.users.find()           // Lista utenti
db.payslips.find()        // Lista buste paga
db.orders.find()          // Lista ordini
db.users.countDocuments() // Conta utenti`}
                        </code>
                      </div>
                    </div>
                  </div>

                  {/* Backup Info */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h3 className="font-semibold mb-2 text-blue-900">Backup Database</h3>
                    <p className="text-sm text-blue-800 mb-2">
                      Lo script di backup è configurato in <code>/app/backend/backup_gdrive.py</code>
                    </p>
                    <div className="text-sm">
                      <p className="text-blue-700 mb-1">Per eseguire un backup manuale:</p>
                      <code className="block bg-blue-100 p-2 rounded text-xs">
                        docker exec busta_app_1 python3 /app/backend/backup_gdrive.py
                      </code>
                    </div>
                  </div>

                  {/* Stats */}
                  <div className="grid grid-cols-3 gap-4">
                    <div className="bg-green-50 p-4 rounded-lg text-center">
                      <Users className="w-8 h-8 mx-auto text-green-600 mb-2" />
                      <div className="text-2xl font-bold text-green-700">{stats?.total_users || 0}</div>
                      <div className="text-sm text-green-600">Utenti</div>
                    </div>
                    <div className="bg-blue-50 p-4 rounded-lg text-center">
                      <FileText className="w-8 h-8 mx-auto text-blue-600 mb-2" />
                      <div className="text-2xl font-bold text-blue-700">{stats?.total_payslips || 0}</div>
                      <div className="text-sm text-blue-600">Buste Paga</div>
                    </div>
                    <div className="bg-amber-50 p-4 rounded-lg text-center">
                      <ShoppingCart className="w-8 h-8 mx-auto text-amber-600 mb-2" />
                      <div className="text-2xl font-bold text-amber-700">{stats?.total_orders || 0}</div>
                      <div className="text-sm text-amber-600">Ordini</div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Modal Crea Utente */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-white rounded-lg max-w-md w-full p-6 shadow-xl space-y-4 max-h-[90vh] overflow-y-auto animate-in fade-in zoom-in duration-200">
            <div className="flex justify-between items-center border-b pb-3">
              <h3 className="text-lg font-bold flex items-center gap-2">
                <Users className="w-5 h-5 text-forest" /> Nuovo Utente
              </h3>
              <button onClick={() => setShowCreateModal(false)} className="text-slate-400 hover:text-slate-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <form onSubmit={handleCreateUser} className="space-y-4 pt-2">
              <div className="space-y-1">
                <label className="text-xs font-semibold text-slate-600">Nome *</label>
                <input
                  type="text"
                  required
                  placeholder="es. Mario Rossi"
                  value={createUserForm.name}
                  onChange={(e) => setCreateUserForm({ ...createUserForm, name: e.target.value })}
                  className="w-full border border-slate-200 rounded-md p-2 text-sm focus:outline-none focus:ring-1 focus:ring-forest"
                />
              </div>

              <div className="space-y-1">
                <label className="text-xs font-semibold text-slate-600">Email *</label>
                <input
                  type="email"
                  required
                  placeholder="es. mario.rossi@esempio.com"
                  value={createUserForm.email}
                  onChange={(e) => setCreateUserForm({ ...createUserForm, email: e.target.value })}
                  className="w-full border border-slate-200 rounded-md p-2 text-sm focus:outline-none focus:ring-1 focus:ring-forest"
                />
              </div>

              <div className="space-y-1">
                <label className="text-xs font-semibold text-slate-600">Password *</label>
                <input
                  type="password"
                  required
                  placeholder="Minimo 6 caratteri"
                  value={createUserForm.password}
                  onChange={(e) => setCreateUserForm({ ...createUserForm, password: e.target.value })}
                  className="w-full border border-slate-200 rounded-md p-2 text-sm focus:outline-none focus:ring-1 focus:ring-forest"
                  minLength={6}
                />
              </div>

              <div className="space-y-1">
                <label className="text-xs font-semibold text-slate-600">Ruolo / Privilegi</label>
                <select
                  value={createUserForm.role}
                  onChange={(e) => setCreateUserForm({ ...createUserForm, role: e.target.value })}
                  className="w-full border border-slate-200 rounded-md p-2 text-sm focus:outline-none focus:ring-1 focus:ring-forest bg-white"
                >
                  <option value="user">Utente Standard</option>
                  <option value="operator">Operatore Sindacale</option>
                  <option value="admin">Amministratore</option>
                </select>
              </div>

              <div className="space-y-1">
                <label className="text-xs font-semibold text-slate-600">Crediti Iniziali</label>
                <input
                  type="number"
                  min="0"
                  value={createUserForm.credits}
                  onChange={(e) => setCreateUserForm({ ...createUserForm, credits: parseInt(e.target.value) || 0 })}
                  className="w-full border border-slate-200 rounded-md p-2 text-sm focus:outline-none focus:ring-1 focus:ring-forest"
                />
              </div>

              <div className="flex justify-end gap-3 border-t pt-4">
                <Button type="button" variant="outline" onClick={() => setShowCreateModal(false)}>
                  Annulla
                </Button>
                <Button type="submit" className="bg-forest hover:bg-forest/90 text-white font-medium" disabled={saving}>
                  {saving ? 'Creazione...' : 'Crea Utente'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Modifica Utente */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-white rounded-lg max-w-md w-full p-6 shadow-xl space-y-4 max-h-[90vh] overflow-y-auto animate-in fade-in zoom-in duration-200">
            <div className="flex justify-between items-center border-b pb-3">
              <h3 className="text-lg font-bold flex items-center gap-2">
                <Edit className="w-5 h-5 text-forest" /> Modifica Utente
              </h3>
              <button onClick={() => setShowEditModal(false)} className="text-slate-400 hover:text-slate-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <form onSubmit={handleUpdateUser} className="space-y-4 pt-2">
              <div className="space-y-1">
                <label className="text-xs font-semibold text-slate-600">Nome *</label>
                <input
                  type="text"
                  required
                  placeholder="es. Mario Rossi"
                  value={editUserForm.name}
                  onChange={(e) => setEditUserForm({ ...editUserForm, name: e.target.value })}
                  className="w-full border border-slate-200 rounded-md p-2 text-sm focus:outline-none focus:ring-1 focus:ring-forest"
                />
              </div>

              <div className="space-y-1">
                <label className="text-xs font-semibold text-slate-600">Email *</label>
                <input
                  type="email"
                  required
                  placeholder="es. mario.rossi@esempio.com"
                  value={editUserForm.email}
                  onChange={(e) => setEditUserForm({ ...editUserForm, email: e.target.value })}
                  className="w-full border border-slate-200 rounded-md p-2 text-sm focus:outline-none focus:ring-1 focus:ring-forest"
                />
              </div>

              <div className="space-y-1">
                <label className="text-xs font-semibold text-slate-600">Password (opzionale)</label>
                <input
                  type="password"
                  placeholder="Lascia vuoto per non modificare"
                  value={editUserForm.password}
                  onChange={(e) => setEditUserForm({ ...editUserForm, password: e.target.value })}
                  className="w-full border border-slate-200 rounded-md p-2 text-sm focus:outline-none focus:ring-1 focus:ring-forest"
                  minLength={6}
                />
              </div>

              <div className="space-y-1">
                <label className="text-xs font-semibold text-slate-600">Ruolo / Privilegi</label>
                <select
                  value={editUserForm.role}
                  onChange={(e) => setEditUserForm({ ...editUserForm, role: e.target.value })}
                  className="w-full border border-slate-200 rounded-md p-2 text-sm focus:outline-none focus:ring-1 focus:ring-forest bg-white"
                >
                  <option value="user">Utente Standard</option>
                  <option value="operator">Operatore Sindacale</option>
                  <option value="admin">Amministratore</option>
                </select>
              </div>

              <div className="space-y-1">
                <label className="text-xs font-semibold text-slate-600">Crediti</label>
                <input
                  type="number"
                  min="0"
                  value={editUserForm.credits}
                  onChange={(e) => setEditUserForm({ ...editUserForm, credits: parseInt(e.target.value) || 0 })}
                  className="w-full border border-slate-200 rounded-md p-2 text-sm focus:outline-none focus:ring-1 focus:ring-forest"
                />
              </div>

              <div className="flex items-center gap-2 pt-2">
                <input
                  type="checkbox"
                  id="edit-user-active"
                  checked={editUserForm.is_active}
                  onChange={(e) => setEditUserForm({ ...editUserForm, is_active: e.target.checked })}
                  className="rounded border-slate-300 text-forest focus:ring-forest h-4 w-4"
                />
                <label htmlFor="edit-user-active" className="text-sm font-medium text-slate-700">Account Attivo</label>
              </div>

              <div className="flex justify-end gap-3 border-t pt-4">
                <Button type="button" variant="outline" onClick={() => setShowEditModal(false)}>
                  Annulla
                </Button>
                <Button type="submit" className="bg-forest hover:bg-forest/90 text-white font-medium" disabled={saving}>
                  {saving ? 'Salvataggio...' : 'Salva Modifiche'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
